import { Injectable, Injector } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CookieUtil } from '@utils/cookies';
import { WheelOption, AmountRange, PrizeType, DynamicPrizeConfig, PrizeTemplate } from '@interface/ruleta.interface';
import { RouletteRequest, RouletteResult, ParticipationConfig, SavedRoulettePrize } from './interfaces/roulette.interface';
import { LocalStorageUtil } from '@utils/localstorage';
import { RouletteConfigService } from './roulette-config.service';

/**
 * Servicio dedicado para gestionar toda la lógica de ruletas
 * Separación de responsabilidades: solo se encarga de ruletas
 */

@Injectable({
  providedIn: 'root',
})
export class RouletteService {

  constructor(
    private ngbModal: NgbModal,
    private cookies: CookieUtil,
    private localStorage: LocalStorageUtil,
    private injector: Injector,
    private rouletteConfigService: RouletteConfigService,
  ) {}

  /**
   * Solicita mostrar la ruleta con configuración dinámica
   * Este es el método central que orquesta toda la lógica de ruleta
   * @param request Configuración de la solicitud de ruleta
   * @returns Promise que resuelve con el resultado completo
   */
  async requestRoulette(request: RouletteRequest): Promise<RouletteResult> {
    try {
      const result: RouletteResult = {
        prize: null,
        participated: false,
        source: request.source,
        timestamp: new Date(),
      };

      if (!this.validateRouletteRequest(request)) {
        console.warn('[RouletteService] Solicitud de ruleta inválida');
        return result;
      }

      if (this.hasUserAlreadyParticipated(request.source)) {
        result.participated = true;
        return result;
      }

      const prize = await this.showRouletteModal(request.wheelOptions, request.wheelCampaign, request.customTermsUrl);

      if (prize) {
        await this.processPrizeWon(prize, request);
        result.prize = prize;
        result.participated = true;
      }

      return result;
    } catch (error) {
      console.error('[RouletteService] Error en requestRoulette:', error);
      return {
        prize: null,
        participated: false,
        source: request.source,
        timestamp: new Date(),
      };
    }
  }

  /**
   * Activa ruleta dinámica desde componentes
   * Genera automáticamente las opciones basado en el monto del usuario
   * @param source Identificador del componente/campaña que solicita la ruleta
   * @param wheelCampaign Identificador de la campaña (opcional)
   * @param userContext Contexto adicional del usuario (opcional)
   * @param customTermsUrl URL personalizada de términos y condiciones (opcional)
   */
  async showDynamicWheelRoulette(source: string, wheelCampaign?: string, userContext?: any, customTermsUrl?: string): Promise<WheelOption | null> {
    try {
      //console.log(`[RouletteService] Iniciando ruleta dinámica para source: ${source}, campaña: ${wheelCampaign || 'default'}`);

      const userAmount = this.getUserAmountFromCookies();
      if (!userAmount || userAmount <= 0) {
        console.warn('No se pudo obtener monto válido del usuario');
        return null;
      }

      const dynamicWheelOptions = this.generateDynamicWheelOptions(userAmount, source);

      const result = await this.requestRoulette({
        source,
        wheelOptions: dynamicWheelOptions,
        wheelCampaign: wheelCampaign || 'default',
        userContext,
        autoRedirect: true,
        customTermsUrl,
      });

      return result.prize;
    } catch (error) {
      console.error('Error en ruleta dinámica:', error);
      return null;
    }
  }

  /**
   * Método de conveniencia para activar ruleta con opciones manuales
   * @param source Identificador del componente/campaña que solicita la ruleta
   * @param wheelOptions Opciones de la ruleta
   * @param wheelCampaign Identificador de la campaña (opcional)
   * @param userContext Contexto adicional del usuario (opcional)
   * @param customTermsUrl URL personalizada de términos y condiciones (opcional)
   */
  async showWheelRoulette(source: string, wheelOptions: WheelOption[], wheelCampaign?: string, userContext?: any, customTermsUrl?: string): Promise<WheelOption | null> {
    const result = await this.requestRoulette({
      source,
      wheelOptions,
      wheelCampaign: wheelCampaign || 'default',
      userContext,
      autoRedirect: true,
      customTermsUrl,
    });

    return result.prize;
  }

  /**
   * Verifica si el usuario puede participar en una ruleta específica
   * @param source Identificador de la fuente/campaña
   * @returns true si puede participar, false si ya participó
   */
  canParticipateInRoulette(source: string): boolean {
    const canParticipate = !this.hasUserAlreadyParticipated(source);
    return canParticipate;
  }

  /**
   * Obtiene el premio guardado en localStorage (si existe)
   * @returns SavedRoulettePrize del premio completo o null si no existe
   */
  getSavedPrize(): SavedRoulettePrize | null {
    try {
      const savedData = this.localStorage.get('selectedPrizeRulette');
      if (!savedData) return null;
      
      const parsed = JSON.parse(savedData);
      
      // Retrocompatibilidad: si es un WheelOption antiguo, convertirlo
      if (parsed.label && !parsed.prize) {
        return {
          prize: parsed,
          customTermsUrl: undefined,
          source: 'legacy',
          timestamp: new Date().toISOString()
        };
      }
      
      // Si ya tiene la nueva estructura, devolverlo tal como está
      return parsed as SavedRoulettePrize;
    } catch (error) {
      return null;
    }
  }

  /**
   * Recupera premio existente cuando el usuario recargó la página
   * Verifica si hay un premio guardado y reactiva el bono automáticamente
   * @returns Promise<WheelOption | null> El premio reactivado o null si no existe
   */
  async recoverExistingPrize(): Promise<WheelOption | null> {
    try {
      const hasParticipated =
        this.cookies.get('ParticipationRuletteApplied') || this.cookies.get('BonusAppliedCorrectly');
      if (!hasParticipated) {
        return null;
      }

      const savedData = this.getSavedPrize();
      if (!savedData) {
        return null;
      }

      const { BonusService } = await import('../bonus/bonus.service');
      const bonusService = this.injector.get(BonusService);

      // Obtener monto del usuario (necesario para el cálculo del bono)
      const userAmount = this.cookies.get('amount');
      const amount = userAmount ? parseInt(userAmount) : 0;

      // Procesar el premio con términos personalizados si existen
      bonusService.processRoulettePrize(savedData.prize, amount, savedData.customTermsUrl);

      return savedData.prize;
    } catch (error) {
      return null;
    }
  }

  /**
   * Limpia el premio guardado
   */
  clearSavedPrize(): void {
    this.localStorage.delete('selectedPrizeRulette');
  }

  /**
   * Genera wheelOptions dinámicamente basado en el monto
   * @param amount Monto de la simulación
   * @param source Fuente de la solicitud (opcional, para logs)
   * @returns Array de WheelOption con premios elegibles y selección aleatoria
   */
  generateDynamicWheelOptions(amount: number, source?: string): WheelOption[] {
    //console.log(`[RouletteService] Generando ruleta dinámica para monto: $${amount.toLocaleString()}`);

    const eligiblePrizes = this.getEligiblePrizeTypes(amount);
    //console.log('Premios elegibles:', eligiblePrizes);

    const weightedPool = this.createWeightedPrizePool(eligiblePrizes);
    const selectedPrizes = this.selectPrizesForWheel(weightedPool);
    const wheelOptions = this.convertToWheelOptions(selectedPrizes);

    /* console.log(
      `Ruleta generada con ${wheelOptions.length} opciones:`,
      wheelOptions.map((w) => w.label),
    ); */
    return wheelOptions;
  }



  /**
   * Determina qué tipos de premios son elegibles según el monto
   */
  private getEligiblePrizeTypes(amount: number): PrizeType[] {
    const range = this.rouletteConfigService.dynamicConfig.amountRanges.find((range) => amount >= range.min && amount <= range.max);

    if (!range) {
      console.warn(`No se encontró rango para monto: ${amount}, usando rango por defecto`);
      return [PrizeType.BONIFICACION_020, PrizeType.SIGUE_PARTICIPANDO];
    }

    return range.eligiblePrizeTypes;
  }

  /**
   * Crea un pool ponderado de premios con sus probabilidades
   */
  private createWeightedPrizePool(eligiblePrizes: PrizeType[]): Array<{ prizeType: PrizeType; weight: number }> {
    return eligiblePrizes.map((prizeType) => ({
      prizeType,
      weight: this.rouletteConfigService.dynamicConfig.baseProbabilities[prizeType] || 0.1,
    }));
  }

  /**
   * Selecciona premios específicos para mostrar en la ruleta
   * Solo opciones únicas, la duplicación será visual en el componente
   */
  private selectPrizesForWheel(weightedPool: Array<{ prizeType: PrizeType; weight: number }>): PrizeType[] {
    const availablePrizes = weightedPool
      .filter((item) => item.prizeType !== PrizeType.SIGUE_PARTICIPANDO)
      .map((item) => item.prizeType);

    const selectedPrizes: PrizeType[] = [...new Set(availablePrizes)];

    //console.log('[RouletteService] Premios únicos seleccionados:', selectedPrizes);
    return selectedPrizes;
  }

  /**
   * Convierte tipos de premios a WheelOptions completas
   */
  private convertToWheelOptions(prizeTypes: PrizeType[]): WheelOption[] {
    return prizeTypes
      .map((prizeType, index) => this.createWheelOptionFromTemplate(prizeType, `option_${index}`))
      .sort(() => Math.random() - 0.5);
  }

  /**
   * Crea una WheelOption desde un template de premio
   */
  private createWheelOptionFromTemplate(prizeType: PrizeType, suffix: string): WheelOption {
    const template = this.rouletteConfigService.prizeTemplates[prizeType];

    return {
      id: `${template.type}_${suffix}_${Date.now()}`,
      key: `${template.type}_${suffix}`,
      label: template.label,
      value: template.value,
      color: template.color,
      textColor: template.textColor,
      bonus_type: template.bonus_type,
      bonusTag: template.bonusTag,
      tag: template.tag,
    };
  }

  /**
   * Determina el tamaño aleatorio de la ruleta
   */
  private getRandomWheelSize(): number {
    const { minOptions, maxOptions } = this.rouletteConfigService.dynamicConfig;
    return Math.floor(Math.random() * (maxOptions - minOptions + 1)) + minOptions;
  }

  /**
   * Obtiene el monto del usuario desde las cookies del simulador
   */
  private getUserAmountFromCookies(): number {
    try {
      const amount = this.cookies.get('amount');
      if (!amount) {
        return 0;
      }
      const numericAmount = parseInt(amount.replace(/\D/g, ''), 10);
      return isNaN(numericAmount) ? 0 : numericAmount;
    } catch (error) {
      console.error('Error obteniendo monto del usuario:', error);
      return 0;
    }
  }

  /**
   * Limpia la cookie de participación (solo para desarrollo/testing)
   */
  clearAllParticipationCookies(): void {
    this.cookies.delete('ParticipationRuletteApplied');
  }

  /**
   * Obtiene estadísticas de participación
   * @param source Fuente específica o undefined para todas
   */
  getParticipationStats(source?: string): { [key: string]: boolean } {
    // TODO: Implementar lógica para obtener estadísticas de participación
    // Por ahora retornamos un objeto simple
    if (source) {
      return {
        [source]: this.hasUserAlreadyParticipated(source),
      };
    }

    return {};
  }

  // ================== MÉTODOS PRIVADOS ==================

  /**
   * Valida que la solicitud de ruleta sea correcta
   */
  private validateRouletteRequest(request: RouletteRequest): boolean {
    if (!request.source || !request.wheelOptions || request.wheelOptions.length === 0) {
      return false;
    }

    // Validar que cada wheelOption tenga las propiedades requeridas
    return request.wheelOptions.every((option) => option.id && option.key && option.label && option.bonus_type);
  }

  /**
   * Verifica si el usuario ya participó en esta ruleta
   */
  private hasUserAlreadyParticipated(source: string): boolean {
    const participationCookie = this.cookies.get('ParticipationRuletteApplied');
    const bonusCookie = this.cookies.get('BonusAppliedCorrectly');

    //console.log(`[RouletteService] Verificando participación para source: ${source}`);
    //console.log('ParticipationRuletteApplied:', participationCookie);
    //console.log('BonusAppliedCorrectly:', bonusCookie);

    const hasParticipated = !!(participationCookie || bonusCookie);

    //console.log(`Usuario ${hasParticipated ? 'YA participó' : 'PUEDE participar'}`);

    return hasParticipated;
  }

  /**
   * Muestra el modal de ruleta con las opciones dinámicas
   */
  private async showRouletteModal(wheelOptions: WheelOption[], wheelCampaign?: string, customTermsUrl?: string): Promise<WheelOption | null> {
    //console.log(`[RouletteService] Abriendo modal de ruleta - Campaña: ${wheelCampaign || 'default'}`);
    
    const { ModalPrizeWheelComponent } = await import('../../modals/modal-prize-wheel/modal-prize-wheel.component');

    const modalRef = this.ngbModal.open(ModalPrizeWheelComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'lg modal-ruleta',
      centered: true,
    });

    modalRef.componentInstance.wheelOptions = wheelOptions;
    modalRef.componentInstance.wheelCampaign = wheelCampaign || 'default';
    modalRef.componentInstance.customTermsUrl = customTermsUrl;

    try {
      const result = await modalRef.result;

      if (result && typeof result === 'object' && result.prize) {
        return result.prize;
      }
      return result || null;
    } catch (dismissed) {
      return null;
    }
  }

  /**
   * Procesa el premio ganado y actualiza el estado
   */
  private async processPrizeWon(prize: WheelOption, request: RouletteRequest): Promise<void> {
    try {
      //console.log('[RouletteService] Procesando premio ganado:', prize.label);

      this.cookies.save('ParticipationRuletteApplied', 'true', 1);
      //console.log('Cookie de participación guardada exitosamente');

      // Guardar premio completo con términos personalizados para recuperación
      const savedPrize: SavedRoulettePrize = {
        prize,
        customTermsUrl: request.customTermsUrl,
        source: request.source,
        timestamp: new Date().toISOString()
      };
      
      this.localStorage.save('selectedPrizeRulette', JSON.stringify(savedPrize));
      //console.log('Premio completo guardado en localStorage para recuperación');
    } catch (error) {
      console.error('Error procesando premio ganado:', error);
    }
  }
}
